import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api } from "../../api";

const fieldActive = (f, d) => Object.entries(f.when || {}).every(([k, allowed]) => allowed.includes(d[k]));
const schemaCache = {};
export const clearSchemaCache = () => Object.keys(schemaCache).forEach(k => delete schemaCache[k]);

export function ProposalRecordEditor({ dept, value, onChange, testId }) {
  const [schema, setSchema] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    schemaCache[dept] = schemaCache[dept] || api.changeRequests.schema(dept);
    schemaCache[dept].then(setSchema).catch(e => { delete schemaCache[dept]; setErr(String(e.message || e)); });
  }, [dept]);
  if (err) return <div style={{ color: "var(--danger)", fontSize: 12 }}>{err}</div>;
  if (!schema) return <div className="muted" style={{ fontSize: 12 }}>Loading fields…</div>;
  const set = (k, v) => {
    const next = { ...value, [k]: v };
    schema.fields.forEach(f => { if (!fieldActive(f, next)) delete next[f.key]; });
    onChange(next);
  };
  const fields = schema.fields.filter(f => !f.locked && fieldActive(f, value));
  return (
    <div data-testid={testId} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
      {fields.map(f => (
        <label key={f.key} style={{ fontSize: 12, gridColumn: f.type === "splitRows" || f.type === "textarea" ? "1 / -1" : undefined }}>
          <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
          <FieldInput f={f} v={value[f.key]} set={(v) => set(f.key, v)} choices={schema.choices} />
        </label>
      ))}
    </div>
  );
}

function FieldInput({ f, v, set, choices }) {
  const tid = `cr-field-${f.key}`;
  const opt = (list) => [<option key="" value="">— None —</option>, ...list.map(o => <option key={o.id} value={o.id}>{o.label}</option>)];
  if (f.type === "select") return <select className="input" value={v ?? ""} onChange={e => set(e.target.value)} data-testid={tid}><option value="">Select…</option>{f.options.map(o => <option key={o}>{o}</option>)}</select>;
  if (f.type === "artistSelect") return <select className="input" value={v ?? ""} onChange={e => set(e.target.value)} data-testid={tid}>{opt(choices.artists)}</select>;
  if (f.type === "releaseSelect") return <select className="input" value={v ?? ""} onChange={e => set(e.target.value)} data-testid={tid}>{opt(choices.releases)}</select>;
  if (f.type === "multiArtistSelect") return <select multiple className="input" style={{ minHeight: 80 }} value={v || []} onChange={e => set([...e.target.selectedOptions].map(o => o.value))} data-testid={tid}>{choices.artists.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}</select>;
  if (f.type === "boolean") return <input type="checkbox" checked={!!v} onChange={e => set(e.target.checked)} data-testid={tid} />;
  if (f.type === "textarea") return <textarea className="input" rows={3} value={v ?? ""} onChange={e => set(e.target.value)} data-testid={tid} />;
  if (f.type === "splitRows") return <SplitRows rows={v || []} roles={f.options} set={set} tid={tid} />;
  const type = f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "url" ? "url" : "text";
  return <input className="input" type={type} step={type === "number" ? "0.01" : undefined} value={v ?? ""} onChange={e => set(type === "number" && e.target.value !== "" ? Number(e.target.value) : e.target.value)} data-testid={tid} />;
}

function SplitRows({ rows, roles, set, tid }) {
  const upd = (i, k, val) => set(rows.map((r, j) => (i === j ? { ...r, [k]: val } : r)));
  const total = rows.reduce((s, r) => s + (Number(r.percent) || 0), 0);
  return (
    <div data-testid={tid}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 90px 40px", gap: 8, marginBottom: 6 }}>
          <input className="input" value={r.name || ""} onChange={e => upd(i, "name", e.target.value)} placeholder="Party name" data-testid={`${tid}-name-${i}`} />
          <select className="input" value={r.role || ""} onChange={e => upd(i, "role", e.target.value)} data-testid={`${tid}-role-${i}`}>{roles.map(x => <option key={x}>{x}</option>)}</select>
          <input className="input" type="number" step="0.01" value={r.percent ?? ""} onChange={e => upd(i, "percent", Number(e.target.value))} data-testid={`${tid}-pct-${i}`} />
          <button type="button" className="btn btn-danger" onClick={() => set(rows.filter((_, j) => j !== i))} style={{ padding: "5px 9px" }} data-testid={`${tid}-del-${i}`}><Trash2 size={12} /></button>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button type="button" className="btn" onClick={() => set([...rows, { name: "", role: roles[0], percent: 0 }])} style={{ fontSize: 12 }} data-testid={`${tid}-add`}><Plus size={12} /> Add party</button>
        <span className={`badge ${Math.abs(total - 100) < 0.01 ? "badge-mint" : "badge-warn"}`}>{total}% of 100%</span>
      </div>
    </div>
  );
}
